/**
 * Gate I13: Animal Summary Generation Drawer
 * 
 * A slide-out drawer for generating new AI summaries with timeframe selection
 * and optional custom questions.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface AnimalSummaryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animalId: string;
  animalName: string | null;
}

type TimeframeType = "all_time" | "last_7_days" | "last_14_days" | "last_30_days";

const TIMEFRAME_OPTIONS: { value: TimeframeType; label: string }[] = [
  { value: "all_time", label: "All time" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
];

export function AnimalSummaryDrawer({
  open,
  onOpenChange,
  animalId,
  animalName,
}: AnimalSummaryDrawerProps) {
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeType>("all_time");
  const [customQuestions, setCustomQuestions] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const generateMutation = useMutation({
    mutationFn: async (params: { timeframe: TimeframeType; customQuestions?: string }) => {
      return apiRequest("POST", `/api/animals/${animalId}/behavior-summary/generate`, params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/animals', animalId, 'behavior-context'] });
      queryClient.invalidateQueries({ queryKey: ['/api/animals', animalId, 'summary-history'] });
      toast({
        title: "Summary generated",
        description: "The behavior summary has been updated.",
      });
      setCustomQuestions("");
      setSelectedTimeframe("all_time");
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate summary. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleGenerate = () => {
    generateMutation.mutate({
      timeframe: selectedTimeframe,
      customQuestions: customQuestions.trim() || undefined,
    });
  };

  const displayName = animalName || animalId;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:w-[450px]"
        data-testid="drawer-summary-generate"
      >
        <SheetHeader>
          <SheetTitle data-testid="drawer-summary-title">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Generate AI Summary
            </div>
          </SheetTitle>
          <SheetDescription data-testid="drawer-summary-description">
            for {displayName} ({animalId})
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium" data-testid="label-timeframe">
              Timeframe
            </Label>
            <RadioGroup
              value={selectedTimeframe}
              onValueChange={(value) => setSelectedTimeframe(value as TimeframeType)}
              className="space-y-2"
              data-testid="radio-group-timeframe"
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value={option.value} 
                    id={option.value}
                    data-testid={`radio-timeframe-${option.value}`}
                  />
                  <Label htmlFor={option.value} className="font-normal cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label htmlFor="customQuestions" className="text-sm font-medium" data-testid="label-custom-questions">
              Custom Questions (optional)
            </Label>
            <Textarea
              id="customQuestions"
              placeholder="Ask specific questions about this animal's behavior..."
              value={customQuestions}
              onChange={(e) => setCustomQuestions(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="textarea-custom-questions"
            />
            <p className="text-xs text-muted-foreground">
              Examples: "Is the dog improving with leash reactivity?" or "What are the main triggers?"
            </p>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="w-full"
            data-testid="button-generate-summary"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Summary
              </>
            )}
          </Button>

          {generateMutation.isError && (
            <p className="text-sm text-destructive text-center" data-testid="text-error-message">
              {generateMutation.error?.message || "An error occurred"}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default AnimalSummaryDrawer;
